import { ActionReducerMapBuilder, CaseReducer, PayloadAction } from "@reduxjs/toolkit";
import type { AdminState } from "../AdminDb";
import { AdminPendingBusiness } from "../../../interfaces/AdminApprovalObject";
import { ApprovalStatus } from "../../../interfaces/OnboardingObject";
import {
  ADMIN_ACCESS_DENIED,
  decideBusiness,
  fetchAdminPendingBusinesses,
  markBusinessPaid,
} from "../../../services/thunks/AdminBusinessesThunk";
import { AdminTabState, createAdminTabState, registerAsync } from "./adminTabState";

// Admin -> Businesses -> Approvals. Everything this tab owns lives in this
// file: state shape, initial state, its reducer cases and its selectors.
// AdminDb only assembles them, so adding a tab never grows the slice file.

export type ApprovalSort = "newest" | "oldest" | "feeDesc";

interface StatusBucket {
  rows: AdminPendingBusiness[];
  /** null = never loaded (as opposed to loaded and empty). */
  lastFetchedAt: number | null;
}

export interface ApprovalsState extends AdminTabState {
  /**
   * One cache per approval status, because this tab fetches PER STATUS rather
   * than fetching everything and filtering client-side the way the captain's
   * own submissions screen does. That screen shows one person's handful of
   * rows; this one is every captain's and only grows. Don't "fix" it back.
   */
  byStatus: Record<ApprovalStatus, StatusBucket>;
  /** Chip selection - tab-local UI state, kept in the store so switching tabs
   *  and coming back doesn't reset the reviewer's place. */
  activeStatus: ApprovalStatus;
  search: string;
  sort: ApprovalSort;
  /**
   * Server said "not yours to see". A STATE, not an error: the tab renders a
   * no-access view instead of a retry button. Same distinction OnboardingDb
   * draws for CAPTAIN_NOT_FOUND.
   */
  accessDenied: boolean;
}

const emptyBucket = (): StatusBucket => ({ rows: [], lastFetchedAt: null });

export const approvalsInitialState = (): ApprovalsState => ({
  ...createAdminTabState(),
  byStatus: {
    PENDING: emptyBucket(),
    APPROVED: emptyBucket(),
    REJECTED: emptyBucket(),
  },
  activeStatus: "PENDING",
  search: "",
  sort: "newest",
  accessDenied: false,
});

// ── Loading/error map keys (shared with the UI, never hand-typed there) ──────

export const approvalsFetchKey = (status: ApprovalStatus): string =>
  `fetchPendingBusinesses_${status}`;

export const approvalsDecideKey = (pendingId: string): string =>
  `decide_${pendingId}`;

// ── Plain reducers (merged into the admin slice by AdminDb) ─────────────────

const setApprovalStatus: CaseReducer<AdminState, PayloadAction<ApprovalStatus>> = (
  state,
  action
) => {
  state.approvals.activeStatus = action.payload;
};

const setApprovalSearch: CaseReducer<AdminState, PayloadAction<string>> = (
  state,
  action
) => {
  state.approvals.search = action.payload;
};

const setApprovalSort: CaseReducer<AdminState, PayloadAction<ApprovalSort>> = (
  state,
  action
) => {
  state.approvals.sort = action.payload;
};

export const approvalsReducers = {
  setApprovalStatus,
  setApprovalSearch,
  setApprovalSort,
};

// ── Async cases ─────────────────────────────────────────────────────────────

export function attachApprovalsCases(
  builder: ActionReducerMapBuilder<AdminState>
): void {
  registerAsync(builder, fetchAdminPendingBusinesses, {
    tab: (state) => state.approvals,
    key: (status) => approvalsFetchKey(status),
    loading: "Loading submissions...",
    onFulfilled: (state, payload) => {
      state.approvals.byStatus[payload.status] = {
        rows: payload.rows,
        lastFetchedAt: Date.now(),
      };
      state.approvals.accessDenied = false;
    },
    onRejected: (state, message) => {
      if (message === ADMIN_ACCESS_DENIED) {
        state.approvals.accessDenied = true;
        return true; // handled: a state, not an error to retry
      }
      return false;
    },
  });

  registerAsync(builder, decideBusiness, {
    tab: (state) => state.approvals,
    key: (arg) => approvalsDecideKey(arg.pendingId),
    loading: "Recording decision...",
    onFulfilled: (state, payload) => {
      const { row, from } = payload;
      const buckets = state.approvals.byStatus;

      // Leave the bucket it came from...
      buckets[from].rows = buckets[from].rows.filter(
        (existing) => existing.pendingId !== row.pendingId
      );

      // ...and land in the one it moved to, but only if that bucket has been
      // loaded. Inserting into a never-loaded bucket would make a list of one
      // look complete; leaving it untouched keeps it honest (it fetches on
      // first open).
      const target = buckets[row.approvalStatus];
      if (target.lastFetchedAt !== null) {
        const at = target.rows.findIndex((e) => e.pendingId === row.pendingId);
        if (at >= 0) target.rows[at] = row;
        else target.rows.unshift(row);
      }
    },
  });

  registerAsync(builder, markBusinessPaid, {
    tab: (state) => state.approvals,
    key: (arg) => approvalsDecideKey(arg.business.pendingId), // use same decide key so UI spins similarly
    loading: "Marking as paid...",
    onFulfilled: (state, payload) => {
      const { row, from } = payload;
      const buckets = state.approvals.byStatus;

      if (from === row.approvalStatus) {
        // Just update in place if it hasn't changed buckets.
        const target = buckets[from];
        if (target.lastFetchedAt !== null) {
          const at = target.rows.findIndex((e) => e.pendingId === row.pendingId);
          if (at >= 0) target.rows[at] = row;
        }
      } else {
        // Leave the bucket it came from...
        buckets[from].rows = buckets[from].rows.filter(
          (existing) => existing.pendingId !== row.pendingId
        );

        // ...and land in the one it moved to
        const target = buckets[row.approvalStatus];
        if (target.lastFetchedAt !== null) {
          const at = target.rows.findIndex((e) => e.pendingId === row.pendingId);
          if (at >= 0) target.rows[at] = row;
          else target.rows.unshift(row);
        }
      }
    },
  });
}

// ── Selectors ───────────────────────────────────────────────────────────────
// `any` state, like SubscriptionsDb's selectors: importing RootState here would
// close a cycle (UiDb -> AdminDb -> this file -> UiDb).

export const selectApprovals = (state: any): ApprovalsState => state.admin.approvals;

export const selectApprovalBucket = (
  state: any,
  status: ApprovalStatus
): StatusBucket => state.admin.approvals.byStatus[status];

export const selectApprovalCounts = (state: any): Record<ApprovalStatus, number> => {
  const byStatus = (state.admin.approvals as ApprovalsState).byStatus;
  return {
    PENDING: byStatus.PENDING.rows.length,
    APPROVED: byStatus.APPROVED.rows.length,
    REJECTED: byStatus.REJECTED.rows.length,
  };
};

/** Rows of the active chip with the tab's search and sort applied. Search
 *  matches name or phone; sorting is stable and never mutates the cache. */
export const selectVisibleApprovals = (state: any): AdminPendingBusiness[] => {
  const approvals = state.admin.approvals as ApprovalsState;
  const term = approvals.search.trim().toLowerCase();
  const rows = approvals.byStatus[approvals.activeStatus].rows;

  const filtered = term
    ? rows.filter(
        (row) =>
          row.businessName.toLowerCase().includes(term) ||
          row.contactPhone.includes(term)
      )
    : rows;

  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (approvals.sort === "feeDesc") return b.finalFee - a.finalFee;
    const at = new Date(a.createdAt).getTime() || 0;
    const bt = new Date(b.createdAt).getTime() || 0;
    return approvals.sort === "oldest" ? at - bt : bt - at;
  });
  return sorted;
};

/** True while THIS row's decision is in flight - keyed per row so the rest of
 *  the list stays interactive. */
export const selectIsDeciding = (state: any, pendingId: string): boolean =>
  !!state.admin.approvals.loadingMap[approvalsDecideKey(pendingId)];
